// AWS Bedrock pricing for Claude, Nova, and Gemini models (per 1K tokens)
// Prices as of January 2025 - update as needed
// See https://aws.amazon.com/bedrock/pricing/ and https://ai.google.dev/pricing

// Pricing tiers based on model family
const PRICING = {
  // Claude 3 Haiku
  haiku3: {
    input: 0.00025,
    output: 0.00125,
    cacheRead: null,
    cacheWrite: null,
  },
  // Claude 3.5 Haiku
  haiku35: {
    input: 0.0008,
    output: 0.004,
    cacheRead: 0.00008,
    cacheWrite: 0.001,
  },
  // Claude 4.5 Haiku
  haiku45: {
    input: 0.001,
    output: 0.005,
    cacheRead: 0.0001,
    cacheWrite: 0.00125,
  },
  // Claude 3 Sonnet
  sonnet3: {
    input: 0.003,
    output: 0.015,
    cacheRead: null,
    cacheWrite: null,
  },
  // Claude 3.5 Sonnet (v1, v2), Claude 3.7 Sonnet, Claude 4 Sonnet, Claude 4.5 Sonnet
  sonnet: {
    input: 0.003,
    output: 0.015,
    cacheRead: 0.0003,
    cacheWrite: 0.00375,
  },
  // Claude 3 Opus
  opus3: {
    input: 0.015,
    output: 0.075,
    cacheRead: null,
    cacheWrite: null,
  },
  // Claude 4 Opus, Claude 4.1 Opus, Claude 4.5 Opus
  opus: {
    input: 0.015,
    output: 0.075,
    cacheRead: 0.0015,
    cacheWrite: 0.01875,
  },

  // Amazon Nova models
  // Nova Micro - text only, fastest/cheapest
  novaMicro: {
    input: 0.000035,
    output: 0.00014,
    cacheRead: null,
    cacheWrite: null,
  },
  // Nova Lite - multimodal, low cost
  novaLite: {
    input: 0.00006,
    output: 0.00024,
    cacheRead: null,
    cacheWrite: null,
  },
  // Nova 2 Lite - next gen lite
  nova2Lite: {
    input: 0.00004,
    output: 0.00016,
    cacheRead: null,
    cacheWrite: null,
  },
  // Nova Pro - multimodal, balanced
  novaPro: {
    input: 0.0008,
    output: 0.0032,
    cacheRead: null,
    cacheWrite: null,
  },
  // Nova Premier - most capable
  novaPremier: {
    input: 0.0025,
    output: 0.0125,
    cacheRead: null,
    cacheWrite: null,
  },

  // Google Gemini models
  // Gemini 2.0 Flash - Best budget choice, high speed, lowest cost
  gemini20Flash: {
    input: 0.00015,
    output: 0.0006,
    cacheRead: null,
    cacheWrite: null,
  },
  // Gemini 2.5 Flash - Best all-rounder, significant intelligence boost over 2.0 Flash
  gemini25Flash: {
    input: 0.0003,
    output: 0.0025,
    cacheRead: null,
    cacheWrite: null,
  },
  // Gemini 2.5 Pro - Complex reasoning, coding agents, difficult RAG tasks
  // Note: 2-tier pricing based on context length (<128k vs >128k)
  gemini25Pro: {
    input: 0.00125, // <128k context
    inputLarge: 0.0025, // >128k context
    output: 0.01,
    cacheRead: null,
    cacheWrite: null,
  },
};

// Default pricing (Sonnet-class, matches current hardcoded values)
const DEFAULT_PRICING = PRICING.sonnet;

/**
 * Get pricing tier for a model ID
 * @param {string} modelId - The model ID (e.g., "anthropic.claude-3-5-sonnet-20241022-v2:0")
 * @returns {Object} Pricing object with input, output, cacheRead, cacheWrite rates per 1K tokens
 */
export const getModelPricing = (modelId) => {
  if (!modelId) {
    return DEFAULT_PRICING;
  }

  const id = modelId.toLowerCase();

  // Match Haiku models
  if (id.includes('haiku')) {
    if (id.includes('haiku-4-5') || id.includes('haiku-45')) {
      return PRICING.haiku45;
    }
    if (id.includes('haiku-3-5') || id.includes('3-5-haiku') || id.includes('35-haiku')) {
      return PRICING.haiku35;
    }
    // Claude 3 Haiku
    return PRICING.haiku3;
  }

  // Match Opus models
  if (id.includes('opus')) {
    if (id.includes('opus-4') || id.includes('claude-4')) {
      return PRICING.opus;
    }
    // Claude 3 Opus
    return PRICING.opus3;
  }

  // Match Sonnet models (default for most use cases)
  if (id.includes('sonnet')) {
    if (id.includes('sonnet-3-20240229') || id.includes('claude-3-sonnet-20240229')) {
      return PRICING.sonnet3;
    }
    // All other Sonnet versions (3.5, 3.7, 4, 4.5) share same pricing
    return PRICING.sonnet;
  }

  // Match Amazon Nova models
  if (id.includes('nova')) {
    if (id.includes('nova-premier')) {
      return PRICING.novaPremier;
    }
    if (id.includes('nova-pro')) {
      return PRICING.novaPro;
    }
    if (id.includes('nova-2-lite')) {
      return PRICING.nova2Lite;
    }
    if (id.includes('nova-lite')) {
      return PRICING.novaLite;
    }
    if (id.includes('nova-micro')) {
      return PRICING.novaMicro;
    }
    // Default Nova to Pro pricing if unrecognized variant
    return PRICING.novaPro;
  }

  // Match Google Gemini models
  if (id.includes('gemini')) {
    // Match Gemini Pro models first (more specific)
    if (id.includes('2.5-pro') || id.includes('2-5-pro') || id.includes('gemini-2.5-pro')) {
      return PRICING.gemini25Pro;
    }
    // Match Gemini Flash models
    if (id.includes('2.5-flash') || id.includes('2-5-flash') || id.includes('gemini-2.5-flash')) {
      return PRICING.gemini25Flash;
    }
    if (id.includes('2.0-flash') || id.includes('2-0-flash') || id.includes('gemini-2.0-flash')) {
      return PRICING.gemini20Flash;
    }
    // Default Gemini to 2.5 Flash pricing if unrecognized variant
    return PRICING.gemini25Flash;
  }

  // Fallback to default (Sonnet pricing)
  return DEFAULT_PRICING;
};

/**
 * Calculate cost estimate based on token usage and model
 * @param {Object} usage - Token usage object with inputTokens, outputTokens, cacheReadInputTokens, cacheWriteInputTokens
 * @param {string} modelId - The model ID
 * @returns {number} Estimated cost in dollars
 *
 * Note: For Gemini 2.5 Pro, this uses the <128k context pricing tier.
 * If you need >128k context pricing, you'll need to manually check pricing.inputLarge.
 */
export const calculateCost = (usage, modelId = null) => {
  const pricing = getModelPricing(modelId);

  const {
    inputTokens = 0,
    outputTokens = 0,
    cacheReadInputTokens = 0,
    cacheWriteInputTokens = 0
  } = usage || {};

  // Convert per-1K-token rates to per-token rates
  const inputCost = inputTokens * (pricing.input / 1000);
  const outputCost = outputTokens * (pricing.output / 1000);

  // Cache pricing (use 0 if not supported for this model)
  const cacheReadCost = pricing.cacheRead
    ? cacheReadInputTokens * (pricing.cacheRead / 1000)
    : 0;
  const cacheWriteCost = pricing.cacheWrite
    ? cacheWriteInputTokens * (pricing.cacheWrite / 1000)
    : 0;

  return inputCost + outputCost + cacheReadCost + cacheWriteCost;
};

/**
 * Get a human-readable pricing description for a model
 * @param {string} modelId - The model ID
 * @returns {string} Description of pricing
 */
export const getPricingDescription = (modelId) => {
  const pricing = getModelPricing(modelId);
  const parts = [
    `$${pricing.input}/1K input`,
    `$${pricing.output}/1K output`
  ];
  if (pricing.cacheRead) {
    parts.push(`$${pricing.cacheRead}/1K cache read`);
  }
  if (pricing.cacheWrite) {
    parts.push(`$${pricing.cacheWrite}/1K cache write`);
  }
  return parts.join(', ');
};